import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

export function useCountUp(target, duration = 1600) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !started.current) {
        started.current = true;
        const start = performance.now();
        const tick = (t) => {
          const p = Math.min((t - start) / duration, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          setVal(Math.floor(eased * target));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [target, duration]);
  return [val, ref];
}

export function SectionHeader({ overline, title, sub, center }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }} transition={{ duration: 0.6 }}
      className={`mb-12 ${center ? "text-center mx-auto max-w-2xl" : "max-w-3xl"}`}>
      {overline && <div className="overline mb-3">{overline}</div>}
      <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-[#FDFBF7]">{title}</h2>
      {sub && <p className="mt-4 text-lg text-[#C8C4B7] leading-relaxed">{sub}</p>}
    </motion.div>
  );
}

export function Field({ label, name, type = "text", required, value, onChange, placeholder, textarea, options, half }) {
  const common = {
    name, value: value ?? "", onChange, required, placeholder,
    className: "gw-input", "data-testid": `field-${name}`,
  };
  return (
    <div className={half ? "" : "sm:col-span-2"}>
      <label className="block text-sm text-[#C8C4B7] mb-2">{label}{required && <span className="text-[#D4AF37]"> *</span>}</label>
      {textarea ? (
        <textarea rows={4} {...common} />
      ) : options ? (
        <select {...common}>
          <option value="">Select…</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input type={type} {...common} />
      )}
    </div>
  );
}
